import {
  Box,
  ListSubheader,
  MenuItem,
  TextField,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { GeneratorStatusExceedanceDetailQuery } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";

import { CalculationSummary } from "./CalculationSummary";
import { DrawerDisclosure } from "./DrawerDisclosure";
import { drawerSectionSx, DrawerSectionHeading } from "./DrawerSection";
import { EpisodicExemptCaption } from "./EpisodicExemptCaption";
import { calculatedStatusLabel, formatLbs, determinationFactors } from "./generatorStatusDisplay";
import { formatPeriodMonth } from "./periodFormat";

type Detail = GeneratorStatusExceedanceDetailQuery["generatorStatusExceedanceDetail"];
type Factors = Record<string, number>;

function RawInputs({
  federal,
  stateFactors,
  showState,
  state,
}: {
  federal: Factors;
  stateFactors: Factors;
  showState: boolean;
  state: string | null;
}) {
  const labels = [
    ...new Set([...Object.keys(federal), ...(showState ? Object.keys(stateFactors) : [])]),
  ];
  if (!labels.length) return null;
  return (
    <TableContainer>
      <Table size="small" aria-label="Calculation inputs">
        <TableHead>
          <TableRow>
            <TableCell>Input (lb)</TableCell>
            <TableCell align="right">Federal</TableCell>
            {showState && <TableCell align="right">{state} state</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {labels.map((label) => (
            <TableRow key={label}>
              <TableCell component="th" scope="row">
                {label.replace(/ \(lbs\)$/, "")}
              </TableCell>
              <TableCell align="right">
                {federal[label] == null ? "—" : formatLbs(federal[label])}
              </TableCell>
              {showState && (
                <TableCell align="right">
                  {stateFactors[label] == null ? "—" : formatLbs(stateFactors[label])}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SourceEvidence({ sources }: { sources: Detail["sources"] }) {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Each source is calculated separately. These quantities are not added together.
      </Typography>
      <TableContainer>
        <Table size="small" aria-label="Federal source calculations">
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Calculated federal</TableCell>
              <TableCell align="right">Hazardous waste (lb)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sources.map((source) => (
              <TableRow key={source.source}>
                <TableCell>{sourceLabel(source.source)}</TableCell>
                <TableCell>{calculatedStatusLabel(source.federalStatus, true)}</TableCell>
                <TableCell align="right">
                  {source.totalHazardousLbs == null ? "—" : formatLbs(source.totalHazardousLbs)}
                  <EpisodicExemptCaption lbs={source} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function sourceLabel(source: string) {
  if (source === "eManifest") return "e-Manifest";
  if (source === "vendor") return "Vendor";
  if (source === "document") return "Reviewed documents";
  if (source === "generation") return "Container fills";
  return source;
}

function knownFactors(entries: [string, number | null | undefined][]): Factors {
  return Object.fromEntries(entries.filter((entry): entry is [string, number] => entry[1] != null));
}

export function CalculationEvidence({
  detail,
  inReview,
  month,
  monthOptions,
  onSelectMonth,
  latestAffectedMonth,
  showState,
  state,
  tenantId,
  facilityId,
}: {
  detail: Detail;
  inReview: boolean;
  month: string;
  monthOptions: { periodStart: string; inReview: boolean }[];
  onSelectMonth: (month: string) => void;
  latestAffectedMonth: string;
  showState: boolean;
  state: string | null;
  tenantId: string;
  facilityId: string;
}) {
  const hasDetailedFactors = month === latestAffectedMonth;
  const selected = detail.calculatedMonths.find((item) => item.periodStart === month);
  const federal = hasDetailedFactors
    ? determinationFactors(detail.determinationFactors, "federal")
    : knownFactors([
        ["Total Hazardous Waste (lbs)", selected?.totalHazardousLbs],
        ["Acute Waste (lbs)", selected?.acuteLbs],
      ]);
  const stateFactors = hasDetailedFactors
    ? determinationFactors(detail.determinationFactors, "state")
    : knownFactors([["State Regulated Waste (lbs)", selected?.stateRegulatedLbs]]);
  const end = DateTime.fromISO(month, { zone: "utc" }).endOf("month").toISODate();
  const reviewMonths = monthOptions.filter((option) => option.inReview);
  const otherMonths = monthOptions.filter((option) => !option.inReview);
  return (
    <Box sx={drawerSectionSx}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
          <DrawerSectionHeading component="h3" sx={{ mb: 0 }}>
            Data used
          </DrawerSectionHeading>
          <TextField
            select
            size="small"
            label="Calculation month"
            value={month}
            onChange={(event) => onSelectMonth(event.target.value)}
            sx={{ minWidth: 200 }}
            slotProps={{
              select: { MenuProps: { slotProps: { paper: { sx: { maxHeight: 320 } } } } },
            }}
          >
            {reviewMonths.length > 0 && <ListSubheader>In this review</ListSubheader>}
            {reviewMonths.map((option) => (
              <MenuItem key={option.periodStart} value={option.periodStart}>
                {formatPeriodMonth(option.periodStart)}
              </MenuItem>
            ))}
            {otherMonths.length > 0 && <ListSubheader>Other months</ListSubheader>}
            {otherMonths.map((option) => (
              <MenuItem key={option.periodStart} value={option.periodStart}>
                {formatPeriodMonth(option.periodStart)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <MuiLink
          component={Link}
          target="_blank"
          rel="noopener noreferrer"
          to={`/o/${tenantId}/waste/facilities/${facilityId}?startDate=${month}&endDate=${end ?? month}`}
          variant="body2"
        >
          Open facility waste data ↗
        </MuiLink>
      </Stack>
      {!inReview && (
        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
          This month is outside the affected months for this review.
        </Typography>
      )}
      <CalculationSummary
        federal={federal}
        stateFactors={stateFactors}
        showState={showState}
        state={state}
        selected={selected}
      />
      {!hasDetailedFactors && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Only monthly summary data is available here. Open facility waste data for the full
          calculation inputs.
        </Typography>
      )}
      {showState && state !== "CA" && state !== "WA" && Object.keys(stateFactors).length > 0 && (
        <DrawerDisclosure title="State calculation inputs">
          <RawInputs federal={{}} stateFactors={stateFactors} showState state={state} />
        </DrawerDisclosure>
      )}
      {hasDetailedFactors && detail.sources.length > 0 && (
        <DrawerDisclosure title="Source details">
          <SourceEvidence sources={detail.sources} />
        </DrawerDisclosure>
      )}
    </Box>
  );
}
